import { usePage } from '@inertiajs/react'

/**
 * Feature-flag keys — mirrors Features::REGISTRY (app/models/features.rb).
 * The registry is the single source of truth; this union exists so a typo'd
 * key is a type error instead of a silently always-on (or always-off) gate.
 * spec/feature_flag_parity_spec.rb fails if this list drifts from the
 * registry, so update both together.
 */
export type FeatureKey =
  | 'module.quote_templates'
  | 'module.quote_requests'
  | 'module.invoices'
  | 'module.conversations'
  | 'module.public_tracking'
  | 'module.announcements'
  | 'module.contact_form'
  | 'module.bulk_io'
  | 'module.data_cleanup'
  | 'module.integrations'
  | 'feature.customer_api'
  | 'feature.scheduled_reports'
  | 'feature.custom_dashboards'
  | 'feature.custom_roles'
  | 'feature.sso'
  | 'feature.open_website_content'
  | 'feature.open_appearance'

/**
 * Whether an optional module/feature is on, from the server-shared `features`
 * page prop (docs/operator-portal-backend/feature-flags.md).
 *
 * EVERY UI entry point into a feature-gated route — nav item, CTA button, link,
 * tab, search result — must be behind this hook (or the nav `feature:` key),
 * or a user can click into an endpoint that 404s (§6.20: UI and server gates
 * move in lockstep).
 *
 * Same semantics as the AppShell nav: only an explicit `false` disables — the
 * base controllers share every flag's state on every page (marketing shares
 * the public subset), so a missing key only happens in tests.
 */
export default function useFeature(key: FeatureKey): boolean {
  const { props } = usePage<{ features?: Record<string, boolean> }>()
  return props.features?.[key] !== false
}
